/*
 * The little markdown Kara's model writes: paragraphs, lists, headings, pipe
 * tables, code, bold and italic. Parsed to plain data and rendered as React
 * nodes (KaraText), never as HTML, so nothing in an answer can inject markup.
 * Anything it does not recognise stays as the literal text.
 */

export type Inline =
  | { type: "text"; text: string }
  | { type: "code"; text: string }
  | { type: "strong" | "em"; children: Inline[] };

export type Block =
  | { type: "p"; lines: Inline[][] }
  | { type: "h"; children: Inline[] }
  | { type: "ul"; items: Inline[][] }
  | { type: "ol"; start: number; items: Inline[][] }
  | { type: "table"; head: Inline[][]; rows: Inline[][][] }
  | { type: "code"; text: string };

const WORD = /[\p{L}\p{N}]/u;

/** Index of the closing `marker` after `from`, or -1; the content must not be empty or start/end with a space. */
function closing(s: string, marker: string, from: number): number {
  let j = s.indexOf(marker, from);
  while (j !== -1) {
    const inner = s.slice(from, j);
    const single = marker.length === 1;
    // A single `*` or `_` must not be half of a double one.
    const doubled = single && (s[j + 1] === marker || s[j - 1] === marker);
    const wordAfter = marker === "_" && WORD.test(s[j + 1] ?? "");
    if (inner.trim() && inner === inner.trim() && !doubled && !wordAfter) return j;
    j = s.indexOf(marker, j + 1);
  }
  return -1;
}

export function parseInline(s: string): Inline[] {
  const out: Inline[] = [];
  let text = "";
  const flush = () => {
    if (text) out.push({ type: "text", text });
    text = "";
  };
  let i = 0;
  while (i < s.length) {
    const ch = s[i] ?? "";
    if (ch === "`") {
      const j = s.indexOf("`", i + 1);
      if (j > i + 1) {
        flush();
        out.push({ type: "code", text: s.slice(i + 1, j) });
        i = j + 1;
        continue;
      }
    }
    if ((ch === "*" || ch === "_") && s[i + 1] === ch) {
      const marker = ch + ch;
      const j = closing(s, marker, i + 2);
      if (j !== -1) {
        flush();
        out.push({ type: "strong", children: parseInline(s.slice(i + 2, j)) });
        i = j + 2;
        continue;
      }
    }
    // `snake_case` and `2*3` stay text: `_` must open at a word edge.
    if ((ch === "*" || ch === "_") && s[i + 1] !== ch && !(ch === "_" && WORD.test(s[i - 1] ?? ""))) {
      const j = closing(s, ch, i + 1);
      if (j !== -1) {
        flush();
        out.push({ type: "em", children: parseInline(s.slice(i + 1, j)) });
        i = j + 1;
        continue;
      }
    }
    text += ch;
    i++;
  }
  flush();
  return out;
}

const FENCE = /^\s*```/;
const HEADING = /^\s*#{1,6}\s+(.*)$/;
const BULLET = /^\s*[-*+•]\s+(.*)$/;
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/;
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

const cells = (line: string) =>
  line
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => parseInline(c.trim()));

const startsBlock = (line: string, next: string | undefined) =>
  FENCE.test(line) ||
  HEADING.test(line) ||
  BULLET.test(line) ||
  NUMBERED.test(line) ||
  RULE.test(line) ||
  (TABLE_ROW.test(line) && next !== undefined && TABLE_SEP.test(next));

export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const at = (k: number) => lines[k] ?? "";
  const more = (k: number) => k < lines.length;
  const blocks: Block[] = [];
  let i = 0;
  while (more(i)) {
    const line = at(i);
    if (!line.trim() || RULE.test(line)) {
      i++;
      continue;
    }
    if (FENCE.test(line)) {
      const body: string[] = [];
      i++;
      while (more(i) && !FENCE.test(at(i))) body.push(at(i++));
      i++; // the closing fence, if there is one
      blocks.push({ type: "code", text: body.join("\n") });
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ type: "h", children: parseInline((heading[1] ?? "").trim()) });
      i++;
      continue;
    }
    if (TABLE_ROW.test(line) && more(i + 1) && TABLE_SEP.test(at(i + 1))) {
      const head = cells(line);
      const rows: Inline[][][] = [];
      i += 2;
      while (more(i) && TABLE_ROW.test(at(i))) rows.push(cells(at(i++)));
      blocks.push({ type: "table", head, rows });
      continue;
    }
    if (BULLET.test(line)) {
      const items: Inline[][] = [];
      let m: RegExpExecArray | null;
      while (more(i) && (m = BULLET.exec(at(i)))) {
        items.push(parseInline((m[1] ?? "").trim()));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }
    const numbered = NUMBERED.exec(line);
    if (numbered) {
      const items: Inline[][] = [];
      let m: RegExpExecArray | null;
      while (more(i) && (m = NUMBERED.exec(at(i)))) {
        items.push(parseInline((m[2] ?? "").trim()));
        i++;
      }
      blocks.push({ type: "ol", start: Number(numbered[1]), items });
      continue;
    }
    const para: Inline[][] = [];
    while (more(i) && at(i).trim() && (para.length === 0 || !startsBlock(at(i), lines[i + 1]))) {
      para.push(parseInline(at(i).trim()));
      i++;
    }
    blocks.push({ type: "p", lines: para });
  }
  return blocks;
}
